/**
 * Bundle Cart — Shopify-native parent_id implementation.
 *
 * Adds main product(s) + unlocked freebies in ONE atomic /cart/add.js batch.
 * The first main variant is the PARENT. Every other main variant and every
 * unlocked freebie is attached as a CHILD via `parent_id`, so Shopify cascades
 * the removal server-side when the parent line goes.
 *
 * Cart shape for "Buy 3 + 2 freebies":
 *   Product A            (parent — no parent_id, carries `Gift Included`)
 *   ├─ Product B         (parent_id = A's variant id)
 *   ├─ Product C         (parent_id = A's variant id)
 *   ├─ Freebie 1         (parent_id = A's variant id)
 *   └─ Freebie 2         (parent_id = A's variant id)
 *
 * Property contract (new):
 *   Gift Included   visible label on the PARENT line only: "🎁 Free Gifts".
 *                   Merchant-configurable via window.__giftPropertyConfig.
 *   (nothing else)  — parent_id replaces _bundle_id, and Shopify's parent-child
 *                   link replaces _product-type.
 *
 * Removed vs. the previous custom reconciler:
 *   reconcile(), prevSnapshot, reconcileLock, pendingReconcile, fingerprint(),
 *   bindCartMutations(), isRemovalRequest(), isInternalRequest(),
 *   cleanupOrphans(), and the PROP_TYPE / PROP_BUNDLE_ID / TYPE_* constants.
 *   Shopify performs the cascade; there is no window.fetch hook here any more.
 *
 * Removal semantics changed deliberately: only removing the PARENT tears the
 * group down. Removing a child main or a single freebie removes just that line.
 *
 * Theme integration notes (Horizon) — unchanged from the previous version:
 *   - Horizon routes declarative handlers through ONE document-level capture
 *     listener that reads the closest `on:submit` at dispatch time. We neutralise
 *     `on:submit` on the forms we own so Horizon's product-form handler bails,
 *     and perform the add ourselves from a capture-phase submit listener.
 *   - There is no `cart:refresh:completed` event. The one cart event is
 *     `cart:update` (ThemeEvents.cartUpdate); the auto-open drawer listens to it
 *     and cart-items-component re-renders from detail.data.sections.
 *   - POST /cart/add.js with an items array is ATOMIC: if any line fails the
 *     whole request 4xx's. So we try main+freebies, then retry main-only.
 *
 * Legacy carts (items still carrying _bundle_id / _product-type / product-type /
 * _bundle_role from the old system) are cleaned out once on page load.
 */
(function () {
  'use strict';

  if (window.__bundleCartInstalled) return;
  window.__bundleCartInstalled = true;

  const CART_ADD_URL = (window.Theme?.routes?.cart_add_url) || '/cart/add.js';
  const CART_CHANGE_URL = (window.Theme?.routes?.cart_change_url) || '/cart/change.js';
  const CART_URL = '/cart.js';

  const DEFAULT_GIFT_KEY = 'Gift Included';

  // Legacy property keys — READ ONLY, used solely to identify old-system lines
  // for the one-shot cleanup below. Nothing writes them any more.
  const LEGACY_KEYS = ['_bundle_id', '_product-type', 'product-type', '_bundle_role'];

  const JSON_HEADERS = { 'Content-Type': 'application/json', Accept: 'application/json' };

  let atcBusy = false;

  // ---------- Utilities ----------

  async function fetchCart() {
    try {
      const res = await fetch(CART_URL, { headers: { Accept: 'application/json' } });
      return res.ok ? await res.json() : { items: [] };
    } catch (_) {
      return { items: [] };
    }
  }

  function getUnlockedOffers() {
    const nodes = document.querySelectorAll('.offer-item.is-unlocked[data-free-variant-id]');
    const seen = new Set();
    const out = [];
    for (const el of nodes) {
      const variantId = Number(el.dataset.freeVariantId);
      if (!variantId || seen.has(variantId)) continue;
      seen.add(variantId);
      out.push({ variantId, label: el.dataset.freeProductLabel || 'Free Gift' });
    }
    return out;
  }

  function getGiftConfig() {
    const cfg = (typeof window !== 'undefined' && window.__giftPropertyConfig) || {};
    return {
      key: (typeof cfg.key === 'string' && cfg.key.trim()) || DEFAULT_GIFT_KEY,
      emoji: typeof cfg.emoji === 'string' ? cfg.emoji : '🎁',
    };
  }

  function buildGiftValue(unlocked) {
    if (!unlocked || !unlocked.length) return null;
    const { emoji } = getGiftConfig();
    const label = 'Free Gifts';
    return emoji ? `${emoji} ${label}` : label;
  }

  /** Section ids that render cart-items (drawer + cart page) — for section rendering. */
  function getSectionIds() {
    const ids = new Set();
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
      if (el.dataset.sectionId) ids.add(el.dataset.sectionId);
    });
    return Array.from(ids).join(',');
  }

  // ---------- Main-variant resolution ----------

  /**
   * Determine the main variant id(s) and per-line quantity for a form.
   * Prefers the bundle component's public API; falls back to DOM scraping;
   * finally falls back to the plain product variant + quantity input.
   * @param {HTMLFormElement} form
   * @returns {{ ids: number[], perItemQuantity: number, bundle: boolean }}
   */
  function getMainSelections(form) {
    const section = form.closest('.shopify-section') || document;

    const bundleComp = section.querySelector('bundle-group-component');
    if (bundleComp && typeof bundleComp.getActiveSelections === 'function') {
      const ids = bundleComp.getActiveSelections();
      if (Array.isArray(ids) && ids.length) return { ids, perItemQuantity: 1, bundle: true };
    }

    const scraped = scrapeActiveTier(section, form);
    if (scraped.length) return { ids: scraped, perItemQuantity: 1, bundle: true };

    const idInput = /** @type {HTMLInputElement|null} */ (form.querySelector('[name="id"]'));
    const id = idInput ? Number(idInput.value) : 0;
    const qtyInput = /** @type {HTMLInputElement|null} */ (form.querySelector('[name="quantity"]'));
    const qty = qtyInput ? Math.max(1, Number(qtyInput.value) || 1) : 1;
    return { ids: id ? [id] : [], perItemQuantity: qty, bundle: false };
  }

  /**
   * Fallback DOM read of the active bundle tier (used only if the component API
   * is unavailable). Mirrors bundle-group-component's tier→variant logic.
   */
  function scrapeActiveTier(section, form) {
    const tiers = section.querySelectorAll('[data-bundle-item]');
    /** @type {Element|null} */
    let active = null;
    for (const tier of tiers) {
      const radio = /** @type {HTMLInputElement|null} */ (tier.querySelector('[data-bundle-radio]'));
      if (radio?.checked || tier.classList.contains('is-selected')) {
        active = tier;
        break;
      }
    }
    if (!active) return [];

    const quantity = Number(active.getAttribute('data-bundle-quantity')) || 1;
    const slots = active.querySelectorAll('[data-bundle-variant-select]');
    const ids = [];
    slots.forEach((sel) => {
      const v = Number(/** @type {HTMLInputElement|HTMLSelectElement} */ (sel).value);
      if (v > 0) ids.push(v);
    });
    if (ids.length === quantity) return ids;

    const idInput = /** @type {HTMLInputElement|null} */ (form.querySelector('[name="id"]'));
    const fallback = idInput ? Number(idInput.value) : 0;

    // A tier with NO variant selectors (typically "Buy 1") must stay on the
    // shopper's own variant — substituting a character would be a silent swap.
    if (!slots.length) {
      if (!fallback) return ids;
      while (ids.length < quantity) ids.push(fallback);
      return ids;
    }

    // P7: pad a short tier from the ordered, in-stock candidate list rendered
    // by blocks/bundle-group.liquid, NOT by repeating the main variant.
    // ⚠️ This scrape is an INDEPENDENT path from bundle-selector.js
    // (#getTierVariantIds) and must agree with it, or a cart written through
    // the fallback path silently reintroduces the duplicates.
    const comp = section.querySelector('bundle-group-component');
    const candidates = String((comp && comp.dataset.bundleCandidates) || '')
      .split(',')
      .map((raw) => Number(raw.trim()))
      .filter((id) => Number.isFinite(id) && id > 0);

    if (candidates.length) {
      const first = candidates.includes(fallback) ? fallback : candidates[0];
      const seq = [first, ...candidates.filter((id) => id !== first)];
      const used = new Set(ids);
      let pointer = 0;
      while (ids.length < quantity) {
        while (pointer < seq.length && used.has(seq[pointer])) pointer++;
        if (pointer < seq.length) {
          const pick = seq[pointer++];
          used.add(pick);
          ids.push(pick);
        } else {
          ids.push(seq[ids.length % seq.length]);
        }
      }
      return ids;
    }

    if (!fallback) return ids;
    while (ids.length < quantity) ids.push(fallback);
    return ids;
  }

  /** Custom `properties[...]` line-item props on the form (e.g. gift-card recipient). */
  function readExtraProperties(form) {
    const out = {};
    for (const [key, value] of new FormData(form).entries()) {
      if (key.startsWith('properties[') && key.endsWith(']')) {
        out[key.slice('properties['.length, -1)] = value;
      }
    }
    return out;
  }

  // ---------- Loader (styles in assets/bundle-cart.css) ----------

  function setButtonLoading(btn, isLoading) {
    if (!btn) return;

    if (isLoading) {
      // Store original content so we can restore it on release.
      if (!btn.dataset.originalContent) {
        btn.dataset.originalContent = btn.innerHTML;
      }

      // Lock button dimensions BEFORE mutating content — prevents layout shift
      // and stops the button from collapsing to an empty box.
      const rect = btn.getBoundingClientRect();
      btn.style.minWidth = `${rect.width}px`;
      btn.style.minHeight = `${rect.height}px`;

      btn.innerHTML = '<span class="bundle-atc-spinner" aria-hidden="true"></span>';
      btn.classList.add('is-loading');
      btn.setAttribute('aria-busy', 'true');
      btn.disabled = true;
    } else {
      if (btn.dataset.originalContent) {
        btn.innerHTML = btn.dataset.originalContent;
        delete btn.dataset.originalContent;
      }
      btn.style.minWidth = '';
      btn.style.minHeight = '';
      btn.classList.remove('is-loading');
      btn.setAttribute('aria-busy', 'false');
      btn.disabled = false;
    }
  }

  function showInlineError(form, message) {
    const errEl = form.querySelector('[ref="addToCartTextError"]');
    if (!errEl) {
      console.error('[bundle-cart]', message);
      return;
    }
    errEl.classList.remove('hidden');
    // Markup is: <span class="svg-wrapper">…</span>{text}. Reuse/append the text node.
    const textNode = errEl.childNodes[2];
    if (textNode) textNode.textContent = message;
    else errEl.appendChild(document.createTextNode(message));
    setTimeout(() => errEl.classList.add('hidden'), 10000);
  }

  // ---------- Cart write ----------

  /** POST one items array. Returns { ok, sections?, body?, error? }. */
  async function postAdd(items) {
    const payload = { items, sections: getSectionIds(), sections_url: window.location.pathname };
    let res;
    try {
      res = await fetch(CART_ADD_URL, { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(payload) });
    } catch (err) {
      return { ok: false, error: { message: 'Network error', description: String(err) } };
    }
    if (!res.ok) {
      const error = await res.json().catch(() => ({}));
      return { ok: false, error };
    }
    const body = await res.json().catch(() => ({}));
    return { ok: true, sections: body.sections, body };
  }

  /**
   * Build the parent_id-shaped items array and add it atomically.
   *
   * - mainVariantIds[0] is the PARENT. Duplicate occurrences of that variant are
   *   folded into the parent line's quantity (Buy 2 of Red+Red → one line, qty 2).
   * - Every OTHER distinct main variant becomes one child line carrying
   *   parent_id = parent variant id, quantity = how many slots chose it.
   * - Every unlocked freebie becomes a child line, quantity 1, same parent_id.
   *
   * On failure with freebies present we retry mains-only, so a bad freebie never
   * costs the customer the main product.
   *
   * @param {HTMLFormElement} form
   */
  async function addBundleAndFreebies(form) {
    const { ids: mainVariantIds, perItemQuantity } = getMainSelections(form);
    if (!mainVariantIds.length) {
      return { ok: false, message: 'Please choose a variant before adding to cart.' };
    }

    const parentVariantId = Number(mainVariantIds[0]);
    const unlocked = getUnlockedOffers();
    const giftValue = buildGiftValue(unlocked);
    const giftCfg = getGiftConfig();
    const extraProps = readExtraProperties(form);

    // Consolidate same-variant duplicates into quantity groups.
    // For a plain (non-bundle) add there is one id and perItemQuantity carries
    // the chosen quantity; each bundle slot counts as perItemQuantity = 1.
    const variantQuantities = new Map();
    for (const vid of mainVariantIds) {
      const id = Number(vid);
      variantQuantities.set(id, (variantQuantities.get(id) || 0) + perItemQuantity);
    }

    const parentQuantity = variantQuantities.get(parentVariantId) || perItemQuantity;

    // Parent line: the only line carrying properties. No parent_id (it IS the parent).
    const mainItems = [
      {
        id: parentVariantId,
        quantity: parentQuantity,
        properties: {
          ...extraProps,
          ...(giftValue ? { [giftCfg.key]: giftValue } : {}),
        },
      },
    ];

    // Child main lines — every distinct main variant that is not the parent.
    for (const [vid, qty] of variantQuantities.entries()) {
      if (vid === parentVariantId) continue;
      mainItems.push({ id: vid, quantity: qty, parent_id: parentVariantId });
    }

    // Child freebie lines.
    const freeItems = unlocked.map((o) => ({
      id: o.variantId,
      quantity: 1,
      parent_id: parentVariantId,
    }));

    // Attempt 1: everything, atomically.
    let result = await postAdd([...mainItems, ...freeItems]);

    // Attempt 2: only freebies failed → retry with the main product(s) alone.
    if (!result.ok && freeItems.length) {
      console.warn('[bundle-cart] atomic add failed; retrying without freebies', result.error);
      result = await postAdd(mainItems);
    }

    if (!result.ok) {
      const err = result.error || {};
      return { ok: false, message: err.description || err.message || 'Could not add to cart.' };
    }

    // Sanity check: the parent must actually be in the response.
    const added = Array.isArray(result.body?.items) ? result.body.items : null;
    if (added && !added.some((it) => Number(it.variant_id ?? it.id) === parentVariantId)) {
      console.warn('[bundle-cart] parent variant missing from /cart/add.js response', added);
    }

    const cart = await fetchCart();
    return { ok: true, cart, sections: result.sections };
  }

  // ---------- UI refresh ----------

  /**
   * Drive Horizon's native cart UI: dispatch a CartAdd-shaped `cart:update` so
   * cart-items-component re-renders (from data.sections) and the auto-open cart
   * drawer opens.
   */
  function dispatchCartUpdate(cart, sections, source) {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart || undefined,
          sourceId: 'bundle-cart',
          data: { source, itemCount: cart?.item_count, sections },
        },
      })
    );
  }

  /**
   * Re-render the cart UI after a silent REMOVAL (legacy cleanup) so no stale
   * ghost rows remain — a ghost row makes Horizon's line-based /cart/change
   * target a line that no longer exists → 422. A removal must never pop the
   * drawer open, so we strip `auto-open` for the duration of the (synchronous)
   * dispatch and restore it immediately after.
   */
  function refreshCartUISilently(cart, sections, source) {
    const drawers = document.querySelectorAll('cart-drawer-component[auto-open]');
    drawers.forEach((d) => d.removeAttribute('auto-open'));
    dispatchCartUpdate(cart, sections, source); // cart:update listeners run synchronously
    drawers.forEach((d) => d.setAttribute('auto-open', ''));
  }

  /** Resolve once the drawer has finished opening, or after a timeout. */
  function waitForDrawerSettled(timeoutMs = 1500) {
    return new Promise((resolve) => {
      const drawer = document.querySelector('cart-drawer-component');
      const dialog = drawer?.querySelector('dialog');
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        clearTimeout(timer);
        observer?.disconnect();
        requestAnimationFrame(() => resolve());
      };
      const timer = setTimeout(finish, timeoutMs);

      // No drawer on this page (e.g. cart page) — settle on the next frame.
      if (!dialog) {
        requestAnimationFrame(finish);
        return;
      }
      if (dialog.open) {
        finish();
        return;
      }
      var observer =
        typeof MutationObserver !== 'undefined'
          ? new MutationObserver(() => {
              if (dialog.open) finish();
            })
          : null;
      observer?.observe(dialog, { attributes: true, attributeFilter: ['open'] });
    });
  }

  // ---------- ATC submit (single capture-phase listener) ----------

  async function onSubmitCapture(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.dataset.bundleCartOwned !== 'true') return;

    // We own this add. Stop Horizon's native path and the browser navigation.
    event.preventDefault();
    if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();

    if (atcBusy) return;
    if (!form.checkValidity()) {
      form.reportValidity?.();
      return;
    }

    const btn = form.querySelector('add-to-cart-component button, button[name="add"], [type="submit"]');
    if (btn?.disabled) return;

    atcBusy = true;
    setButtonLoading(btn, true);

    try {
      const result = await addBundleAndFreebies(form);
      if (!result.ok) {
        showInlineError(form, result.message);
        return; // do NOT open the drawer on a rolled-back add
      }
      dispatchCartUpdate(result.cart, result.sections, 'bundle-cart');
      await waitForDrawerSettled();
    } catch (err) {
      console.error('[bundle-cart] ATC failed', err);
      showInlineError(form, 'Sorry, could not add to cart. Please try again.');
    } finally {
      setButtonLoading(btn, false);
      atcBusy = false;
    }
  }

  // ---------- Legacy cleanup (page load, one shot, silent) ----------

  /** Remove a line by key; requests section HTML so the last call can refresh UI. */
  async function changeLine(key) {
    try {
      const res = await fetch(CART_CHANGE_URL, {
        method: 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify({
          id: key,
          quantity: 0,
          sections: getSectionIds(),
          sections_url: window.location.pathname,
        }),
      });
      return res.ok ? await res.json().catch(() => null) : null;
    } catch (err) {
      console.warn('[bundle-cart] legacy cleanup failed for', key, err);
      return null;
    }
  }

  /**
   * Customers who added a bundle under the old reconciler still have lines
   * carrying _bundle_id / _product-type / product-type / _bundle_role. Those
   * groupings mean nothing now (nothing cascades them), so we clear them out
   * once on load and leave the customer with a clean cart.
   *
   * The shipping-protection line carries none of these keys and is never touched.
   */
  async function cleanupLegacyItems() {
    const cart = await fetchCart();
    const legacyItems = cart.items.filter((it) => {
      const props = it.properties || {};
      return LEGACY_KEYS.some((k) => props[k]);
    });

    if (!legacyItems.length) return;

    console.info('[bundle-cart] cleaning up', legacyItems.length, 'legacy cart item(s) from the previous system');

    let lastResponse = null;
    for (const it of legacyItems) {
      lastResponse = await changeLine(it.key);
    }

    // Re-sync the cart UI so no ghost rows remain after the silent removal.
    refreshCartUISilently(await fetchCart(), lastResponse?.sections, 'bundle-cart-legacy-cleanup');
  }

  // ---------- Wiring ----------

  /**
   * Claim the main product form(s) that live alongside a bundle or offer group,
   * and neutralise Horizon's declarative `on:submit` so only we perform the add.
   */
  function claimForms() {
    document.querySelectorAll('product-form-component form').forEach((form) => {
      const section = form.closest('.shopify-section') || document;
      if (!section.querySelector('offer-group, bundle-group-component')) return;
      form.dataset.bundleCartOwned = 'true';
      // Empty on:submit → Horizon's delegated handler finds no method and bails.
      form.setAttribute('on:submit', '');
    });
  }

  function init() {
    // Attach the ATC interceptor first so a failure in cleanup can't leave a
    // claimed form without a handler.
    document.addEventListener('submit', onSubmitCapture, { capture: true });
    document.addEventListener('shopify:section:load', claimForms);

    claimForms();

    cleanupLegacyItems();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();